import {
  breadcrumbList,
  organization,
  type SeoRoute,
  webPage,
  webSite,
} from "@furistic/sdk-fe-seo";

import { URLS } from "./lib/config";

const SITE = URLS.SITE;

/**
 * Channel detection (was `src/app/layout.tsx`): production is the only indexed,
 * unbadged channel. `VITE_SITE_URL` is baked per channel by CI (staging builds
 * point at the pages.dev host); `VITE_ENV=staging` is the explicit override.
 * Read through `process.env` where it exists (the vite.config import graph,
 * where `import.meta.env` is absent) and through `import.meta.env` in app code.
 */
function channelEnv(name: string): string | undefined {
  if (typeof process !== "undefined") return process.env?.[name];
  return undefined;
}
const CHANNEL_SITE = channelEnv("VITE_SITE_URL") ?? SITE;
const IS_STAGING =
  channelEnv("VITE_ENV") === "staging" ||
  /staging|pages\.dev/.test(CHANNEL_SITE);
const MODE = (import.meta as { env?: { MODE?: string } }).env?.MODE;
const TITLE_PREFIX = IS_STAGING
  ? "[STG] "
  : MODE === "development"
    ? "[DEV] "
    : "";

/** Applies the layout.tsx title-template prefix to a page title. */
function branded(title: string): string {
  return `${TITLE_PREFIX}${title}`;
}

const ROOT_DESCRIPTION =
  "Furistic builds AI-powered vertical operating systems for real estate, trading, standards audits, waste services, and restaurants — RealtyOS, TradeOS, ConformOS, Haulyard and Foodium, on one shared platform with Otto, the shared AI assistant.";

/** Site identity, shared by every page's structured data. */
export const ORG = organization({
  url: SITE,
  name: "Furistic Technologies Inc.",
  logo: `${SITE}/favicon.svg`,
  sameAs: [],
});
const SITE_NODE = webSite({
  url: SITE,
  name: "Furistic",
  description: ROOT_DESCRIPTION,
});

/** `WebPage` schema with the site identity attached — the baseline for every route. */
function pageSchema(path: string, name: string, description?: string) {
  return webPage({
    url: new URL(path, SITE).toString(),
    name,
    description,
    isPartOf: SITE_NODE,
  });
}

/** Breadcrumb for a route: Home → … → page. */
function breadcrumb(path: string, name: string) {
  const crumbs = [{ name: "Home", path: "/" }];
  const segments = path.split("/").filter(Boolean);
  let trail = "";
  for (const segment of segments) {
    trail += `/${segment}`;
    crumbs.push({
      name:
        trail === path
          ? name
          : segment.charAt(0).toUpperCase() + segment.slice(1),
      path: trail,
    });
  }
  return breadcrumbList(crumbs, SITE);
}

/**
 * Route table: every URL the site serves, with its page meta and schema.org
 * structured data. This is both the react-router table (`@/routes`) and the
 * prerender/sitemap/robots contract — one entry per route, sourced from the
 * App Router metadata it replaces (`src/app/layout.tsx` title template
 * `%s | Furistic` is applied to every title; the old `sitemap.tsx` supplies
 * lastmod/changefreq/priority; the old `robots.tsx` disallow list becomes
 * `disallowPaths` in vite.config.ts).
 */
export const seoRoutes: SeoRoute[] = [
  {
    path: "/",
    meta: {
      title: branded("Furistic | AI-Powered Operating Systems for Industry"),
      description: ROOT_DESCRIPTION,
      image: "/og.png",
      changefreq: "weekly",
      priority: 1.0,
      jsonLd: [SITE_NODE, ORG],
    },
  },
  {
    path: "/legal/accessibility",
    meta: {
      title: branded("Accessibility Statement | Furistic"),
      description:
        "Accessibility Statement for Furistic Technologies Inc. Our commitment to making our platform and products usable for everyone.",
      changefreq: "monthly",
      priority: 0.3,
      lastmod: "2026-03-18",
      jsonLd: [
        pageSchema(
          "/legal/accessibility",
          "Accessibility Statement",
          "Accessibility Statement for Furistic Technologies Inc.",
        ),
        breadcrumb("/legal/accessibility", "Accessibility Statement"),
        ORG,
      ],
    },
  },
  {
    path: "/legal/disclaimer",
    meta: {
      title: branded("AI Disclaimer | Furistic"),
      description:
        "AI Disclaimer for Furistic Technologies Inc. Understand the limitations of AI across RealtyOS, TradeOS, ConformOS, Haulyard, Foodium, and our platform.",
      changefreq: "monthly",
      priority: 0.5,
      lastmod: "2026-03-18",
      jsonLd: [
        pageSchema(
          "/legal/disclaimer",
          "AI Disclaimer",
          "AI Disclaimer for Furistic Technologies Inc.",
        ),
        breadcrumb("/legal/disclaimer", "AI Disclaimer"),
        ORG,
      ],
    },
  },
  {
    path: "/legal/privacy",
    meta: {
      title: branded("Privacy Policy | Furistic"),
      description:
        "Privacy Policy for Furistic Technologies Inc. Learn how we collect, use, and protect your data across RealtyOS, TradeOS, ConformOS, Haulyard, Foodium, and our platform services.",
      changefreq: "monthly",
      priority: 0.5,
      lastmod: "2026-03-18",
      jsonLd: [
        pageSchema(
          "/legal/privacy",
          "Privacy Policy",
          "Privacy Policy for Furistic Technologies Inc.",
        ),
        breadcrumb("/legal/privacy", "Privacy Policy"),
        ORG,
      ],
    },
  },
  {
    path: "/legal/security",
    meta: {
      title: branded("Security & Infrastructure | Furistic"),
      description:
        "Learn about Furistic's security practices, infrastructure architecture, compliance certifications, and responsible disclosure program.",
      changefreq: "monthly",
      priority: 0.5,
      lastmod: "2026-03-18",
      jsonLd: [
        pageSchema(
          "/legal/security",
          "Security & Infrastructure",
          "Furistic security practices, infrastructure architecture, and compliance certifications.",
        ),
        breadcrumb("/legal/security", "Security & Infrastructure"),
        ORG,
      ],
    },
  },
  {
    path: "/legal/terms",
    meta: {
      title: branded("Terms of Service | Furistic"),
      description:
        "Terms of Service for Furistic Technologies Inc. and its products including RealtyOS and TradeOS.",
      changefreq: "monthly",
      priority: 0.5,
      lastmod: "2026-03-18",
      jsonLd: [
        pageSchema(
          "/legal/terms",
          "Terms of Service",
          "Terms of Service for Furistic Technologies Inc.",
        ),
        breadcrumb("/legal/terms", "Terms of Service"),
        ORG,
      ],
    },
  },
];
